import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../utils/constants';
import { showErrorToast, showSuccessToast } from '../utils/toast';
import * as jobService from '../services/jobService';

// Get all jobs query
export const useJobs = (filters = {}, options = {}) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.JOBS.LIST, filters],
    queryFn: () => jobService.getJobs(filters),
    ...options,
  });
};

// Get job by ID query
export const useJob = (jobId, options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.JOBS.BY_ID(jobId),
    queryFn: () => jobService.getJobById(jobId),
    enabled: !!jobId && (options.enabled !== false),
    ...options,
  });
};

// Post job mutation (employer)
export const usePostJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobData) => jobService.postJob(jobData),
    onSuccess: () => {
      // Invalidate queries to refresh data after posting
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS.LIST });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOB_POSTINGS.LIST });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CALENDAR.EVENTS });
      showSuccessToast('Job posted successfully!');
    },
    onError: (error) => {
      showErrorToast(error, 'Failed to post job. Please try again.');
    },
  });
};

// Get matches query
export const useMatches = (userId, userRole, options = {}) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.JOBS.MATCHES, userId, userRole],
    queryFn: () => jobService.getMatches(userId, userRole),
    enabled: !!userId && !!userRole && (options.enabled !== false),
    ...options,
  });
};

// Get match by ID query
export const useMatch = (matchId, options = {}) => {
  return useQuery({
    queryKey: QUERY_KEYS.JOBS.MATCH_BY_ID(matchId),
    queryFn: () => jobService.getMatchById(matchId),
    enabled: !!matchId && (options.enabled !== false),
    ...options,
  });
};

// Accept match mutation
export const useAcceptMatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ matchId, userRole }) => jobService.acceptMatch(matchId, userRole),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS.MATCHES });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS.MATCH_BY_ID(variables.matchId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CALENDAR.EVENTS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS.LIST });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOB_POSTINGS.LIST });
      showSuccessToast('Job accepted! You and the employer have been notified.');
    },
    onError: (error) => {
      showErrorToast(error, 'Failed to accept match. Please try again.');
    },
  });
};

// Reject match mutation
export const useRejectMatch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ matchId, userRole }) => jobService.rejectMatch(matchId, userRole),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS.MATCHES });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS.MATCH_BY_ID(variables.matchId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CALENDAR.EVENTS });
      showSuccessToast('Match rejected.');
    },
    onError: (error) => {
      showErrorToast(error, 'Failed to reject match. Please try again.');
    },
  });
};

// Get potential jobs for employee
export const usePotentialJobs = (employeeId, options = {}) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.JOBS.LIST, 'potential', employeeId],
    queryFn: () => jobService.getPotentialJobs(employeeId),
    enabled: !!employeeId && (options.enabled !== false),
    ...options,
  });
};

// Get job postings for an employer (real API)
export const useEmployerJobPostings = (employerId, options = {}) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.JOB_POSTINGS.LIST, 'employer', employerId],
    queryFn: () => jobService.getEmployerJobPostings(employerId),
    enabled: !!employerId && (options.enabled !== false),
    ...options,
  });
};

// Get job matches for an employee (real API)
export const useEmployeeJobMatches = (options = {}) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.JOBS.MATCHES, 'employee'],
    queryFn: () => jobService.getEmployeeJobMatches(),
    ...options,
  });
};

// Delete job posting mutation (employer only)
export const useDeleteJobPosting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (jobPostingId) => jobService.deleteJobPosting(jobPostingId),
    onSuccess: () => {
      // Invalidate queries to refresh data after deletion
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOB_POSTINGS.LIST });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.JOBS.LIST });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.CALENDAR.EVENTS });
      showSuccessToast('Job posting deleted successfully.');
    },
    onError: (error) => {
      showErrorToast(error, 'Failed to delete job posting. Please try again.');
    },
  });
};











